// Angular imports
import { Component, OnInit, OnDestroy, ContentChildren, QueryList, Output, EventEmitter, ViewChild, ElementRef, HostListener } from '@angular/core';
import { trigger, state, transition, animate, style } from '@angular/animations';

// Import rxjs
import { BehaviorSubject, Observable, Subject, Subscription, takeUntil } from 'rxjs';

// Import undo service
import { ApplicationEventService } from '../../services/application-event.service';
import { ApplicationContextService } from '../../services/application-context.service';

// Import components
import { TabPageComponent } from '../tab-page/tab-page.component';

// Import types
import { CalculationLineApi, CalculationMetaApi } from '../../types';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state-management/app-state';
import * as CalculationActions from 'src/app/state-management/calculation-actions';
import { selectCalculationMeta, selectTemplateDictionary, selectViewlines } from 'src/app/state-management/calculation-selectors';
import { isCalculationLineApi, ViewModelLine } from 'src/app/state-management/calculation-state';

// Constants
const CODE_REGEX = /^\d{1,2}\.?(?:\d{1,2})?$/;

declare interface Subcategory {
    code: string,
    description: string
}

declare interface Category {
    code: string,
    description: string,
    subcategories: Subcategory[]
}

@Component({
    selector: 'tab-view',
    templateUrl: './tab-view.component.html',
    styleUrls: ['./tab-view.component.scss'],
    animations: [
        trigger('side-menu', [
            state('closed', style({
                right: '-35ch'
            })),
            state('open', style({
                right: '0'
            })),
            transition('closed <=> open', [
                animate('.15s ease-in-out')
            ])
        ])
    ],
    standalone: false
})
export class TabViewComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();

    @ContentChildren(TabPageComponent) public pages: QueryList<TabPageComponent>;
    @ViewChild('navigatorRoot') public navigatorRootElement: ElementRef<HTMLDivElement>;
    @ViewChild('navSearchInput') public _navSearchInputElement: ElementRef<HTMLInputElement>;

    @Output() public tabChange = new EventEmitter<number>();

    public selectedIndex = 0;

    public _sideNavigatorOpen = false;
    public _calculationLines: BehaviorSubject<CalculationLineApi[]>;
    public _calculationDescription: string;
    public _visibleCategories: { code: string, description: string, subcategories: { code: string, description: string }[] }[] = [];
    public _openCategories: string[] = [];
    public _viewModelLines: ViewModelLine[] = [];

    /**
     * The meta information object of the active calculation
     *
     * @type {Observable<CalculationMetaApi>}
     * @memberof TabViewComponent
     */
    public meta$: Observable<CalculationMetaApi>;

    /**
     * Mapping from (sub)category code to description
     *
     * @type {{[code: string]: string}}
     * @memberof TabViewComponent
     */
    public templateDict: { [code: string]: string } = {};

    public activeProjectId$ = new BehaviorSubject<string>('');

    public _navSearchText: string = '';

    private subscriptions: Subscription[];

    constructor(
        private applicationEventService: ApplicationEventService,
        private applicationContextService: ApplicationContextService,
        private store: Store<AppState>) {
        // Fill with initial empty value
        this._calculationLines = new BehaviorSubject([]);

        this.meta$ = this.store.select(selectCalculationMeta).pipe(
            takeUntil(this.unsubscribe$)
        )

        this.meta$.subscribe(
            (meta) => {
                this.activeProjectId$.next(meta?.projectId);
                this._calculationDescription = meta?.description ?? '';
            }
        )

        // Link to lines
        this.store.select(selectViewlines).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(viewModelLines => {
            this._viewModelLines = viewModelLines;
            this._calculationLines.next(viewModelLines.filter(f => isCalculationLineApi(f.line)).map<CalculationLineApi>(l => l.line as CalculationLineApi));
        });

        // Update search
        this._calculationLines.pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(lines => {
            this._navSearchText = '';
            this._searchNav();
        })

        this.store.select(selectTemplateDictionary).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(template => {
            this.templateDict = template;

            // Update search window
            this._searchNav();
        })
    }

    public ngOnInit() {
        // this._calculationLines = this.applicationDataService.activeCalculation;

        // // Populate search
        // if (this._calculationLines)
        //     this._searchNav();

        this.subscriptions = [
            this.applicationContextService.get('activeTable').subscribe(table => {
                this._sideNavigatorOpen = false;

                // Update application context
                this.applicationContextService.set('sideNavigatorOpen', this._sideNavigatorOpen);
            }),
            this.applicationEventService.get('search').subscribe(() => {
                if (this._sideNavigatorOpen) return;

                this._sideNavigatorOpen = true;

                // Focus input
                setTimeout(() => this._navSearchInputElement.nativeElement.focus(), 150);

                // Update application context
                this.applicationContextService.set('sideNavigatorOpen', this._sideNavigatorOpen);
            })
        ];
    }

    public ngOnDestroy() {
        // Unsubscribe
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    public selectTab(index: number) {
        const prevTab = this.selectedIndex;

        this.selectedIndex = index;
        this.tabChange.emit(index);
    }

    public _openCategory(code: string) {
        this._openCategories = [...this._openCategories, code];
    }

    public _closeCategory(code: string) {
        if (!this._openCategories.includes(code)) return;

        this._openCategories.splice(this._openCategories.indexOf(code), 1);
        this._openCategories = [...this._openCategories];
    }

    public _toggleCategoryOpen(code: string) {
        // Check if category is open
        const open = this._openCategories.includes(code);

        if (!open) {
            this._openCategories = [...this._openCategories, code];
        } else {
            this._openCategories.splice(this._openCategories.indexOf(code), 1);
            this._openCategories = [...this._openCategories];
        }
    }

    public _scrollToSubcategory(subcategory: Subcategory) {
        // this.applicationEventService.emit('scrollToSubcategory', subcategory);
        this._sideNavigatorOpen = false;

        // Set focus to first line in category
        const firstLine = this._viewModelLines?.find(f => f.code === subcategory.code);

        if (firstLine) {
            this.store.dispatch(CalculationActions.updateSelection({
                selection: {
                    current: {
                        row: firstLine.index,
                        column: 1
                    },
                    selectedLines: [firstLine.line]
                }
            }))
        }

        // Update application context
        this.applicationContextService.set('sideNavigatorOpen', this._sideNavigatorOpen);
    }

    public _scrollToCategory(category: Category) {
        // Set focus to first line in category
        const firstLine = this._viewModelLines?.find(f => f.code === category.code);

        if (firstLine) {
            this.store.dispatch(CalculationActions.updateSelection({
                selection: {
                    current: {
                        row: firstLine.index,
                        column: 1
                    },
                    selectedLines: [firstLine.line]
                }
            }))
        }
        this._sideNavigatorOpen = false;

        // Update application context
        this.applicationContextService.set('sideNavigatorOpen', this._sideNavigatorOpen);
    }

    public _openSideNavigator() {
        setTimeout(() => {
            this._sideNavigatorOpen = true;

            // Update application context
            this.applicationContextService.set('sideNavigatorOpen', this._sideNavigatorOpen);
        });
    }

    public _searchNav(): void {
        if (!this._calculationLines) return;

        // Get input search text
        const search = this._navSearchText.toLowerCase().trim();

        // Set up search for code
        const code = this.splitCode(search);

        // Get all categories from lines
        const allHits = search ? this._calculationLines.value?.filter(l => {
            const [lineCat, lineSub] = l.code.split('.');

            // Include if search input matches line code
            if (lineCat?.includes(code[0]) && (!code[1] || l.code.includes(`${code[0]}.${code[1]}`))) {
                return true;
            }

            // In other cases, the description must contain the search text for the line to be included in the results
            return l.description.toLowerCase().includes(search);
        }) : [...(this._calculationLines.value ?? [])];

        // Now get the subcategories
        const visibleSubcategories = allHits.map(h => h.code).filter((c, i, a) => a.indexOf(c) === i);
        const visibleCategories = [...visibleSubcategories].map(s => s.split('.')[0]).filter((c, i, a) => a.indexOf(c) === i);

        // Now the template is needed to get the descriptions of the (sub)categories
        this._visibleCategories = visibleCategories.map<Category>(cat => ({
            code: cat,
            description: this.templateDict[cat],
            subcategories: visibleSubcategories
                .filter(sub => sub.startsWith(cat))
                .map(sub => ({
                    code: sub,
                    description: this.templateDict[sub]
                }))
        }))

        this._openCategories = this._visibleCategories.map(c => c.code);
    }

    /* --- Helpers --- */

    private splitCode(input: string): [string, string] {
        // Set up search for code
        let code: [string, string] = ['--', '--'];

        // Check if search is (possibly) code
        if (CODE_REGEX.test(input)) {
            const codeSearch = input.replace('.', '');

            if (codeSearch.length <= 2)
                code = [codeSearch.substr(0, Math.min(codeSearch.length, 2)), ''];
            else
                code = [
                    codeSearch.substr(0, 2),
                    codeSearch.substr(2, Math.min(codeSearch.length - 2, 2))
                ];
        }

        return code;
    }

    /* --- Event handlers --- */

    @HostListener('document:click', ['$event'])
    public onClick(event: MouseEvent) {
        // Ignore event if closed
        if (!this._sideNavigatorOpen) return;

        // Check whether or not the click even was in the side menu or not
        const clickedChildElement = this.navigatorRootElement.nativeElement.contains(event.target as Node);

        // Ignore any clicks in the menu
        if (clickedChildElement) return;

        // Close
        this._sideNavigatorOpen = false;

        // Update application context
        this.applicationContextService.set('sideNavigatorOpen', this._sideNavigatorOpen);
    }

    @HostListener('window:keydown.enter', ['$event'])
    public _handleGlobalEnter(event: KeyboardEvent) {
        // Ignore even if closed
        if (!this._sideNavigatorOpen) return;

        // Stop propagation
        event.stopPropagation();

        // Close
        this._sideNavigatorOpen = false;

        // Update application context
        this.applicationContextService.set('sideNavigatorOpen', this._sideNavigatorOpen);
    }

    public _handleEnter(event: KeyboardEvent) {
        //     if (this._visibleCategories.length === 0) return;

        //     // Stop propagation
        //     event.stopPropagation();

        //     // Get input search text
        //     const search = this._navSearchText.toLowerCase().trim();

        //     // Set up search for code
        //     const code = this.splitCode(search);

        //     // Find the first macthing subcategory
        //     let sub: Subcategory | undefined;
        //     this._visibleCategories.find(cat => {
        //         sub = cat.subcategories.find(sub => sub.code === `${code[0]}.${code[1]}` || sub.description.toLowerCase().includes(search));

        //         return !!sub;
        //     });

        //     // If a sub matched, scroll to it
        //     if (sub) {
        //         // Clear search text
        //         this._navSearchText = '';

        //         // Search nav
        //         this._searchNav();

        //         // Scroll
        //         return this._scrollToSubcategory(sub);
        //     }

        //     // Find the first matching category
        //     const cat = this._visibleCategories.find(cat => cat.code.includes(code[0]) || cat.description.toLowerCase().includes(search));

        //     // Scroll to category
        //     if (cat) {
        //         // Clear search text
        //         this._navSearchText = '';

        //         // Search nav
        //         this._searchNav();

        //         // Scroll
        //         return this._scrollToCategory(cat);
        //     }
    }
}
